import React, { useState, useEffect } from 'react';
import { Model } from '@shiftly/shared/src/schema';
import { Alert, AlertTitle, AlertDescription } from '../../ui/alert';
import { TriangleAlert, Check, ChevronsUpDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../ui/command';
import { useSwapModel, useSwapModelInLaunchSlot } from '../../../lib/supabase/formats/supabaseModelDailyFormatService';
import { useModelFormat } from '../../../lib/supabase/formats/supabaseModelFormatService';
import { toast } from 'react-hot-toast';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { DateTime } from 'luxon';

interface DailyModelListSlot {
  id: string;
  position: number;
  time: string;
  slot_period: 'all' | 'am' | 'pm';
  type: 'interaction' | 'promo' | 'text';
  model_id?: string;
  group_id?: string;
  old_model_id?: string;
  metadata?: { text?: string; type?: 'REFERRAL' | 'TOP' | 'SUB_100' };
}

interface FormatListProps {
  slots?: DailyModelListSlot[];
  models?: Model[];
  currentModel?: Model;
  isLoading?: boolean;
  canEdit?: boolean;
  selectedTimezone: string;
  timezoneOffsetHours?: number;
  date?: DateTime;
  onTimezoneChange?: (timezone: string) => void;
  timezoneOptions?: Array<{ label: string; value: string; rawOffsetInMinutes: number }>;
}

const GROUP_COLORS = {
  'A': 'bg-pink-100 border-pink-300 dark:bg-pink-500/80 dark:border-pink-400',
  'B': 'bg-blue-100 border-blue-300 dark:bg-blue-800/30 dark:border-blue-600',
  'C': 'bg-green-100 border-green-300 dark:bg-green-800/30 dark:border-green-600',
  'D': 'bg-purple-100 border-purple-300 dark:bg-purple-800/30 dark:border-purple-600',
  '1': 'bg-orange-100 border-orange-300 dark:bg-orange-800/30 dark:border-orange-600',
  '2': 'bg-red-100 border-red-300 dark:bg-red-800/30 dark:border-red-600',
  '3': 'bg-cyan-100 border-cyan-300 dark:bg-cyan-800/30 dark:border-cyan-600',
  'Unlock': 'bg-emerald-100 border-emerald-300 dark:bg-emerald-800/30 dark:border-emerald-600',
  'Top': 'bg-yellow-100 border-yellow-300 dark:bg-yellow-800/30 dark:border-yellow-600',
  'Test': 'bg-gray-100 border-gray-300 dark:bg-gray-800/30 dark:border-gray-600',
  'Referral': 'bg-slate-100 border-slate-300 dark:bg-slate-800/30 dark:border-slate-600',
} as const;

const GROUP_KEY_COLORS = {
  'A': 'bg-pink-200 text-pink-900 dark:bg-pink-500 dark:text-pink-100',
  'B': 'bg-blue-200 text-blue-900 dark:bg-blue-800 dark:text-blue-100',
  'C': 'bg-green-200 text-green-900 dark:bg-green-800 dark:text-green-100',
  'D': 'bg-purple-200 text-purple-900 dark:bg-purple-800 dark:text-purple-100',
  '1': 'bg-orange-200 text-orange-900 dark:bg-orange-800 dark:text-orange-100',
  '2': 'bg-red-200 text-red-900 dark:bg-red-800 dark:text-red-100',
  '3': 'bg-cyan-200 text-cyan-900 dark:bg-cyan-800 dark:text-cyan-100',
  'Unlock': 'bg-emerald-200 text-emerald-900 dark:bg-emerald-800 dark:text-emerald-100',
  'Top': 'bg-yellow-200 text-yellow-900 dark:bg-yellow-800 dark:text-yellow-100',
  'Test': 'bg-gray-200 text-gray-900 dark:bg-gray-800 dark:text-gray-100',
  'Referral': 'bg-slate-200 text-slate-900 dark:bg-slate-700 dark:text-slate-100',
} as const;

const REFERRAL_COLORS = {
  background: 'bg-slate-100 text-slate dark:bg-slate-700 dark:text-slate',
  key: 'bg-slate-100 text-slate dark:bg-slate-700 dark:text-slate',
} as const;

const formatDisplayTime = (time: string) => {
  const [hours, minutes] = time.split(':');

  // Format as 12-hour time without AM/PM
  const hour = parseInt(hours);
  const displayHour = hour === 0 ? 12 : (hour > 12 ? hour - 12 : hour);

  return `${displayHour.toString().padStart(2, '0')}:${minutes}`;
};

const shiftSlotsByTimezone = (slots: DailyModelListSlot[], timezoneOffsetHours?: number): DailyModelListSlot[] => {
  if (!slots || slots.length === 0 || !timezoneOffsetHours) {
    return slots;
  }

  if (timezoneOffsetHours === 0) {
    return slots;
  }

  // Convert each time by adding timezone offset and create new slots with converted times
  const convertedSlots = slots.map((slot) => {
    const [hours, minutes] = slot.time.split(':');
    let originalHour24 = parseInt(hours);

    // Convert 12-hour to 24-hour format (12:XX becomes 00:XX)
    if (originalHour24 === 12) {
      originalHour24 = 0;
    }

    // Apply timezone offset
    let newHour24 = (originalHour24 + timezoneOffsetHours) % 24;
    if (newHour24 < 0) {
      newHour24 += 24;
    }

    // Convert back to 12-hour format
    let newHour12;
    if (newHour24 === 0) {
      newHour12 = 12;
    } else if (newHour24 > 12) {
      newHour12 = newHour24 - 12;
    } else {
      newHour12 = newHour24;
    }

    const newTime = `${newHour12.toString().padStart(2, '0')}:${minutes}`;

    return {
      ...slot,
      time: newTime,
      _sortKey: newHour12 === 12 ? parseInt(minutes) : newHour12 * 60 + parseInt(minutes)
    };
  });

  // Sort by time, with 12:XX times (which have _sortKey starting at 0) first
  const sortedSlots = convertedSlots.sort((a, b) => {
    return a._sortKey - b._sortKey;
  });

  // Remove the temporary sort key
  return sortedSlots.map(({ _sortKey, ...slot }) => slot);
};


const formatSimpleSlotDisplay = (slot: DailyModelListSlot, models?: Model[]): string => {
  switch (slot.type) {
    case 'interaction':
      return 'Interaction';
    case 'text':
      return slot.metadata?.text || 'Unknown';
    case 'promo':
      if (slot.model_id && models) {
        const targetModel = models.find(m => m.id === slot.model_id);
        if (targetModel) {
          return `${targetModel.name} Promo`;
        }
      }
      return 'Self Swap';
    default:
      return '';
  }
};

const getEffectiveModel = (
  slot: DailyModelListSlot,
  models: Model[] | undefined,
  modelFormat: any
): { originalModel?: Model; launchModel?: Model; hasLaunchSlot: boolean; isLaunchSlot: boolean } => {
  const hasLaunchSlot = !!(slot.old_model_id && slot.model_id);

  // Check if this is a launch slot by finding the matching format item OR if it has both old_model_id and model_id
  let isLaunchSlot = hasLaunchSlot; // If it has both IDs, it's definitely a launch slot

  if (!isLaunchSlot && modelFormat?.items) {
    const formatItem = modelFormat.items.find((item: any) => item.time === slot.time);
    if (formatItem) {
      if (formatItem.type === 'conditional') {
        // Check both AM and PM for launch_group
        const period = slot.slot_period;
        if (period === 'am' && formatItem.am?.launch_group) {
          isLaunchSlot = true;
        } else if (period === 'pm' && formatItem.pm?.launch_group) {
          isLaunchSlot = true;
        } else if (period === 'all' && (formatItem.am?.launch_group || formatItem.pm?.launch_group)) {
          isLaunchSlot = true;
        }
      } else if (formatItem.launch_group) {
        isLaunchSlot = true;
      }
    }
  }

  // When launch slot is active: old_model_id is original, model_id is the launch model
  // When no launch slot: model_id is the original model
  const originalModel = hasLaunchSlot && slot.old_model_id && models ?
    models.find(m => m.id === slot.old_model_id) :
    slot.model_id && models ? models.find(m => m.id === slot.model_id) : undefined;

  const launchModel = hasLaunchSlot && slot.model_id && models ?
    models.find(m => m.id === slot.model_id) : undefined;

  return {
    originalModel,
    launchModel,
    hasLaunchSlot,
    isLaunchSlot
  };
};

const renderModelSelectOrText = (
  slot: DailyModelListSlot,
  models: Model[] | undefined,
  canEdit: boolean,
  modelFormat: any,
  currentModel?: Model,
  onSwap?: (position: number, newModelId: string) => void,
  onLaunchSwap?: (position: number, newModelId: string) => void,
  onRemoveLaunchSlot?: (position: number) => Promise<void>
): React.ReactNode => {
  if (slot.type === 'promo' && models && canEdit) {
    // Get effective model information (potential launch slot)
    const { launchModel, hasLaunchSlot, isLaunchSlot } = getEffectiveModel(slot, models, modelFormat);

    // Regular promo handling with launch slot support
    if ((slot.old_model_id || slot.model_id) && onSwap) {
      // When there's a launch slot active: old_model_id goes in original position, model_id goes in launch position
      // When no launch slot: just use model_id in original position
      const originalModelId = hasLaunchSlot ? slot.old_model_id! : slot.model_id!;
      const targetModel = models.find(m => m.id === originalModelId);

      if (targetModel && targetModel.scheduling_group) {
        const isReferral = isReferralSlot(slot, modelFormat);
        const backgroundColor = getGroupBackgroundColor(targetModel.scheduling_group, isReferral);

        const originalPromoContent = (
          <span className={`px-2 py-1 rounded ${backgroundColor} ${hasLaunchSlot ? 'line-through opacity-60' : ''}`}>
            <ModelSelect
              currentModelId={originalModelId}
              currentModelName={targetModel.name}
              group={targetModel.scheduling_group}
              models={models}
              currentModel={currentModel}
              onSwap={(newModelId) => onSwap && onSwap(slot.position, newModelId)}
              disabled={hasLaunchSlot}
            />
            <span> Promo</span>
            {slot.slot_period !== 'all' && <span> {slot.slot_period.toUpperCase()}</span>}
          </span>
        );

        const launchContent = hasLaunchSlot && launchModel ? (
          <span className="px-2 py-1 rounded bg-brand-gold border-brand-gold dark:bg-brand-dark/30 dark:border-brand-gold">
            <span className="text-brand-dark dark:text-brand-gold font-medium">
              {launchModel.name} (LAUNCH)
            </span>
            {slot.slot_period !== 'all' && <span> {slot.slot_period.toUpperCase()}</span>}
            {canEdit && onRemoveLaunchSlot && (
              <button
                onClick={() => onRemoveLaunchSlot(slot.position)}
                className="ml-2 text-xs text-brand-dark dark:text-brand-gold hover:text-brand-red dark:hover:text-brand-red"
                title="Remove launch slot"
              >
                ✕
              </button>
            )}
          </span>
        ) : isLaunchSlot && onLaunchSwap ? (
          // If this is a launch slot but no model is assigned yet, show launch slot selector
          <span className="px-2 py-1 rounded bg-brand-gold border-brand-gold dark:bg-brand-dark/30 dark:border-brand-gold">
            <LaunchSlotSelect
              launchGroup={modelFormat?.items?.find((item: any) => item.time === slot.time)?.launch_group || 1}
              models={models}
              currentModel={currentModel}
              onLaunchSwap={(newModelId) => onLaunchSwap(slot.position, newModelId)}
            />
            {slot.slot_period !== 'all' && <span> {slot.slot_period.toUpperCase()}</span>}
          </span>
        ) : null;

        // Render based on what we have
        if (isLaunchSlot) {
          if (hasLaunchSlot) {
            // Launch slot with active swap: show original (crossed out) → launch model
            return (
              <div className="flex items-center gap-2">
                {originalPromoContent}
                <span className="text-muted-foreground">→</span>
                {launchContent}
              </div>
            );
          } else {
            // Launch slot without swap: show original promo OR launch slot selector
            return (
              <div className="flex items-center gap-2">
                {originalPromoContent}
                <span className="text-muted-foreground">OR</span>
                {launchContent}
              </div>
            );
          }
        }

        return originalPromoContent;
      }
    }
  }

  const { hasLaunchSlot } = getEffectiveModel(slot, models, null);
  const displayModelId = hasLaunchSlot ? slot.old_model_id : slot.model_id;
  const isReferral = slot.type === 'promo' ? isReferralSlot(slot, modelFormat) : false;
  const backgroundColor = slot.type === 'promo' && displayModelId && models ?
    getGroupBackgroundColor(models.find(m => m.id === displayModelId)?.scheduling_group, isReferral) : '';
  return (
    <span className={`px-2 py-1 rounded ${backgroundColor}`}>
      {formatSimpleSlotDisplay(slot, models)}
      {slot.slot_period !== 'all' && <span> {slot.slot_period.toUpperCase()}</span>}
    </span>
  );
};

const getSlotBackgroundColor = (slot: DailyModelListSlot, models?: Model[], modelFormat?: any): string => {
  if (slot.type === 'promo' && models) {
    // Check if this is a referral slot first
    if (modelFormat && isReferralSlot(slot, modelFormat)) {
      return REFERRAL_COLORS.background;
    }

    const { hasLaunchSlot } = getEffectiveModel(slot, models, null);
    const displayModelId = hasLaunchSlot ? slot.old_model_id : slot.model_id;

    if (displayModelId) {
      const targetModel = models.find(m => m.id === displayModelId);
      if (targetModel?.scheduling_group) {
        return GROUP_COLORS[targetModel.scheduling_group as keyof typeof GROUP_COLORS] || '';
      }
    }
  }
  return '';
};

const getGroupBackgroundColor = (group?: string, isReferral?: boolean): string => {
  if (isReferral) {
    return REFERRAL_COLORS.background;
  }
  if (group) {
    return GROUP_COLORS[group as keyof typeof GROUP_COLORS] || '';
  }
  return '';
};

const isReferralSlot = (slot: DailyModelListSlot, modelFormat: any): boolean => {
  if (!modelFormat?.items || slot.type !== 'promo') {
    return false;
  }

  const formatItem = modelFormat.items.find((item: any) => item.time === slot.time);
  if (!formatItem) {
    return false;
  }

  // Check for specific model reference in format item
  if (formatItem.type === 'conditional') {
    // Check both AM and PM for specific model
    const period = slot.slot_period;
    if (period === 'am' && formatItem.am?.model) {
      return true;
    } else if (period === 'pm' && formatItem.pm?.model) {
      return true;
    } else if (period === 'all' && (formatItem.am?.model || formatItem.pm?.model)) {
      return true;
    }
  } else if (formatItem.model) {
    return true;
  }

  return false;
};

const getPromoGroup = (slot: DailyModelListSlot, models?: Model[], modelFormat?: any): string | undefined => {
  if (slot.type === 'promo' && models) {
    // Check if this is a referral slot first
    if (modelFormat && isReferralSlot(slot, modelFormat)) {
      return 'Referral';
    }

    const { hasLaunchSlot } = getEffectiveModel(slot, models, null);
    const displayModelId = hasLaunchSlot ? slot.old_model_id : slot.model_id;

    if (displayModelId) {
      const targetModel = models.find(m => m.id === displayModelId);
      return targetModel?.scheduling_group;
    }
  }
  return undefined;
};

const groupSlotsByTime = (slots: DailyModelListSlot[]): { [time: string]: DailyModelListSlot[] } => {
  return slots.reduce((acc, slot) => {
    if (!acc[slot.time]) {
      acc[slot.time] = [];
    }
    acc[slot.time].push(slot);
    return acc;
  }, {} as { [time: string]: DailyModelListSlot[] });
};

const SkeletonItem = () => (
  <div className="px-6 py-3 flex items-center gap-4">
    <div className="w-16 h-4 bg-brand-dark dark:bg-brand-dark rounded animate-pulse" />
    <div className="flex-1 h-4 bg-brand-dark dark:bg-brand-dark rounded animate-pulse" />
  </div>
);

interface ModelSelectProps {
  currentModelId: string;
  currentModelName: string;
  group: string;
  models: Model[];
  currentModel?: Model;
  onSwap: (newModelId: string) => void;
  disabled?: boolean;
}

const ModelSelect = ({ currentModelId, currentModelName, models, currentModel, onSwap, disabled }: ModelSelectProps) => {
  const [open, setOpen] = useState(false);
  const availableModels = models
    .filter(model =>
      model.id !== currentModelId &&
      model.scheduling_group &&
      model.id !== currentModel?.id // Exclude the current model whose format we're viewing
    )
    .sort((a, b) => {
      // Sort by scheduling group first, then by name
      if (a.scheduling_group !== b.scheduling_group) {
        return a.scheduling_group!.localeCompare(b.scheduling_group!);
      }
      return a.name.localeCompare(b.name);
    });

  if (availableModels.length === 0 || disabled) {
    return <span>{currentModelName}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span className="cursor-pointer hover:underline decoration-dotted">
          {currentModelName}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <CommandInput placeholder="Search models..." />
          <CommandList>
            <CommandEmpty>No models found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value={currentModelName}
                onSelect={() => {
                  setOpen(false);
                }}
                disabled
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    "opacity-100"
                  )}
                />
                <div className="flex items-center justify-between w-full">
                  <span className="text-muted-foreground">{currentModelName} (Current)</span>
                  {models.find(m => m.id === currentModelId)?.scheduling_group && (
                    <span className="text-xs text-muted-foreground ml-2">
                      Group {models.find(m => m.id === currentModelId)?.scheduling_group}
                    </span>
                  )}
                </div>
              </CommandItem>
              {availableModels.map((model) => (
                <CommandItem
                  key={model.id}
                  value={model.name}
                  onSelect={() => {
                    onSwap(model.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      "opacity-0"
                    )}
                  />
                  <div className="flex items-center justify-between w-full">
                    <span>{model.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      Group {model.scheduling_group}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

interface LaunchSlotSelectProps {
  launchGroup: number;
  models: Model[];
  currentModel?: Model;
  onLaunchSwap: (modelId: string) => void;
  disabled?: boolean;
}

const LaunchSlotSelect = ({ launchGroup, models, currentModel, onLaunchSwap, disabled }: LaunchSlotSelectProps) => {
  const [open, setOpen] = useState(false);
  const oneWeekAgo = DateTime.utc().minus({ days: 7 });

  const availableModels = models
    .filter(model =>
      model.scheduling_group &&
      model.id !== currentModel?.id && // Exclude the current model whose format we're viewing
      model.created_at && model.created_at > oneWeekAgo && // Model must be less than 1 week old
      model.account_type == 'Normal'
    )
    .sort((a, b) => {
      // Sort by scheduling group first, then by name
      if (a.scheduling_group !== b.scheduling_group) {
        return a.scheduling_group!.localeCompare(b.scheduling_group!);
      }
      return a.name.localeCompare(b.name);
    });

  if (availableModels.length === 0 || disabled) {
    return <span className="text-brand-dark dark:text-brand-gold font-medium">LAUNCH {launchGroup}</span>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <span className="cursor-pointer hover:underline decoration-dotted text-brand-dark dark:text-brand-gold font-medium">
          LAUNCH {launchGroup}
        </span>
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0">
        <Command>
          <CommandInput placeholder="Search models..." />
          <CommandList>
            <CommandEmpty>No models found.</CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="launch-slot"
                onSelect={() => {
                  setOpen(false);
                }}
                disabled
              >
                <Check
                  className={cn(
                    "mr-2 h-4 w-4",
                    "opacity-100"
                  )}
                />
                <div className="flex items-center justify-between w-full">
                  <span className="text-muted-foreground">LAUNCH {launchGroup}</span>
                </div>
              </CommandItem>
              {availableModels.map((model) => (
                <CommandItem
                  key={model.id}
                  value={model.name}
                  onSelect={() => {
                    onLaunchSwap(model.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      "opacity-0"
                    )}
                  />
                  <div className="flex items-center justify-between w-full">
                    <span>{model.name}</span>
                    <span className="text-xs text-muted-foreground ml-2">
                      Group {model.scheduling_group}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

export const FormatList = ({
  slots,
  models,
  currentModel,
  isLoading,
  canEdit = false,
  selectedTimezone,
  timezoneOffsetHours,
  date,
  onTimezoneChange,
  timezoneOptions = []
}: FormatListProps) => {
  const { mutateAsync: swapModel, isPending: isSwapping } = useSwapModel();
  const { mutateAsync: swapModelInLaunchSlot } = useSwapModelInLaunchSlot();
  const { data: modelFormat } = useModelFormat(currentModel?.id || '');
  const [timezonePopoverOpen, setTimezonePopoverOpen] = useState(false);

  const getStorageKey = () => {
    if (!currentModel?.id || !date) return null;
    const dateStr = date.toISO()?.split('T')[0];
    return `format-checks-${currentModel.id}-${dateStr}`;
  };

  const [checkmarksEnabled, setCheckmarksEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem('format-checkmarks-enabled');
    return stored ? JSON.parse(stored) : false;
  });

  const [checkedSlots, setCheckedSlots] = useState<Set<string>>(() => {
    const key = getStorageKey();
    if (!key) return new Set();
    const stored = localStorage.getItem(key);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  });

  useEffect(() => {
    const key = getStorageKey();
    if (!key) {
      setCheckedSlots(new Set());
      return;
    }
    const stored = localStorage.getItem(key);
    setCheckedSlots(stored ? new Set(JSON.parse(stored)) : new Set());
  }, [currentModel?.id, date]);

  const toggleCheckmarksEnabled = () => {
    const newValue = !checkmarksEnabled;
    setCheckmarksEnabled(newValue);
    localStorage.setItem('format-checkmarks-enabled', JSON.stringify(newValue));
  };

  const toggleCheck = (slotId: string) => {
    setCheckedSlots(prev => {
      const next = new Set(prev);
      if (next.has(slotId)) {
        next.delete(slotId);
      } else {
        next.add(slotId);
      }

      const key = getStorageKey();
      if (key) {
        localStorage.setItem(key, JSON.stringify([...next]));
      }

      return next;
    });
  };

  let finalSlots = slots || [];

  // Apply timezone-based shifting
  if (finalSlots.length > 0) {
    finalSlots = shiftSlotsByTimezone(finalSlots, timezoneOffsetHours);
  }

  const handleSwap = async (
    position: number,
    newModelId: string
  ) => {
    if (!currentModel || !date || !models) return;

    // Prevent swapping with the current model (the one whose format we're viewing)
    if (newModelId === currentModel.id) {
      toast.error('Cannot swap with the current model');
      return;
    }

    // Find the slot by position
    const slot = finalSlots.find(s => s.position === position);
    if (!slot) {
      toast.error('Slot not found');
      return;
    }

    const loadingToast = toast.loading('Swapping model...');

    try {
      await swapModel({
        slotId: slot.id,
        newModelId,
        includeInHistory: true
      });

      const newModelName = models.find(m => m.id === newModelId)?.name || newModelId;
      toast.success(`Successfully swapped to ${newModelName}`);
    } catch (error) {
      toast.error('Failed to swap model');
      console.error('Swap error:', error);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleLaunchSwap = async (position: number, newModelId: string) => {
    if (!currentModel || !date || !models) return;

    // Prevent swapping with the current model (the one whose format we're viewing)
    if (newModelId === currentModel.id) {
      toast.error('Cannot swap with the current model');
      return;
    }

    // Find the slot by position
    const slot = finalSlots.find(s => s.position === position);
    if (!slot) {
      toast.error('Slot not found');
      return;
    }

    const newModelName = models.find(m => m.id === newModelId)?.name || newModelId;
    const loadingToast = toast.loading(`Setting launch slot to ${newModelName}...`);

    try {
      await swapModelInLaunchSlot({
        slotId: slot.id,
        newModelId,
        reverse: false
      });
      toast.success(`Launch slot set to ${newModelName}`);
    } catch (error) {
      toast.error('Failed to set launch slot');
      console.error('Launch slot error:', error);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleRemoveLaunchSlot = async (position: number) => {
    if (!currentModel || !date || !models) return;

    // Find the slot by position
    const slot = finalSlots.find(s => s.position === position);
    if (!slot) {
      toast.error('Slot not found');
      return;
    }

    const loadingToast = toast.loading('Removing launch slot...');

    try {
      await swapModelInLaunchSlot({
        slotId: slot.id,
        newModelId: slot.old_model_id || '',
        reverse: true
      });
      toast.success('Launch slot removed');
    } catch (error) {
      toast.error('Failed to remove launch slot');
      console.error('Remove launch slot error:', error);
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  if (isLoading) {
    return (
      <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
        {Array.from({ length: 5 }).map((_, index) => (
          <SkeletonItem key={index} />
        ))}
      </div>
    );
  }

  if (!finalSlots?.length) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground dark:text-muted-foreground">
          No schedule slots found
        </p>
      </div>
    );
  }

  const promoGroups = [...new Set(
    finalSlots
      .map(slot => getPromoGroup(slot, models, modelFormat))
      .filter(Boolean)
  )].sort();

  const groupedSlots = groupSlotsByTime(finalSlots);

  return (
    <div className="space-y-2">
      <div>
        <div className="pl-6 py-3 bg-brand-dark dark:bg-brand-dark border-t border-brand-border dark:border-brand-border flex items-center justify-between">
          <div className="flex items-center gap-4">
            {onTimezoneChange && timezoneOptions.length > 0 && (
              <div className="flex items-center">
                <Popover open={timezonePopoverOpen} onOpenChange={setTimezonePopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={timezonePopoverOpen}
                      className="w-64 justify-between"
                      size="sm"
                    >
                      {selectedTimezone || "Select timezone..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-0" align="start">
                    <Command>
                      <CommandInput placeholder="Search timezones..." />
                      <CommandList>
                        <CommandEmpty>No timezone found.</CommandEmpty>
                        <CommandGroup>
                          {timezoneOptions.map((timezone) => (
                            <CommandItem
                              key={timezone.value}
                              onSelect={() => {
                                onTimezoneChange(timezone.value);
                                setTimezonePopoverOpen(false);
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  selectedTimezone === timezone.value ? "opacity-100" : "opacity-0"
                                )}
                              />
                              {timezone.label} (BST{timezone.rawOffsetInMinutes < 0 ? '' : '+'}{timezone.rawOffsetInMinutes / 60})
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={toggleCheckmarksEnabled}
              className="flex items-center px-2"
            >
              <Checkbox checked={checkmarksEnabled} />
              <span>Enable Checkmarks</span>
            </Button>
          </div>
        </div>

        {promoGroups.length > 0 && (
          <div className="px-6 py-4 bg-brand-dark dark:bg-brand-dark border-t border-b border-brand-border dark:border-brand-border">
            <h3 className="text-sm font-medium text-brand-dark dark:text-foreground mb-3">
              Promo Group Colors
            </h3>
            <div className="flex flex-wrap gap-2">
              {promoGroups.map((group) => (
                <span
                  key={group}
                  className={`px-2 py-1 text-xs font-medium rounded ${GROUP_KEY_COLORS[group as keyof typeof GROUP_KEY_COLORS] || 'bg-brand-dark text-foreground dark:bg-brand-dark dark:text-zinc-200'}`}
                >
                  Group {group}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
        {Object.entries(groupedSlots)
          .sort(([timeA], [timeB]) => {
            const [hoursA, minutesA] = timeA.split(':').map(Number);
            const [hoursB, minutesB] = timeB.split(':').map(Number);
            const timeAMinutes = (hoursA === 12 ? 0 : hoursA) * 60 + minutesA;
            const timeBMinutes = (hoursB === 12 ? 0 : hoursB) * 60 + minutesB;
            return timeAMinutes - timeBMinutes;
          })
          .map(([time, timeSlots]) => {
            const sortedSlots = timeSlots.sort((a, b) => a.position - b.position);
            const bgColor = sortedSlots.length === 1 ? getSlotBackgroundColor(sortedSlots[0], models, modelFormat) : '';

            return (
              <div
                key={time}
                className={`px-6 py-1.5 flex items-center gap-4 ${bgColor ? `${bgColor} border-l-4` : ''}`}
              >
                {checkmarksEnabled && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Checkbox
                      checked={checkedSlots.has(`${sortedSlots[0].id}-1`)}
                      onCheckedChange={() => toggleCheck(`${sortedSlots[0].id}-1`)}
                      title="Mark first check"
                    />
                    <Checkbox
                      checked={checkedSlots.has(`${sortedSlots[0].id}-2`)}
                      onCheckedChange={() => toggleCheck(`${sortedSlots[0].id}-2`)}
                      title="Mark second check"
                    />
                  </div>
                )}

                <span className="w-16 font-mono shrink-0 tabular-nums text-foreground dark:text-muted-foreground">
                  {formatDisplayTime(time)}
                </span>

                {sortedSlots.length > 1 ? (
                  <div className="flex items-center gap-2 truncate text-brand-dark dark:text-foreground">
                    {sortedSlots.map((slot, index) => (
                      <React.Fragment key={slot.id}>
                        {index > 0 && <span>/</span>}
                        {renderModelSelectOrText(slot, models, canEdit && !isSwapping && !!date, modelFormat, currentModel, handleSwap, handleLaunchSwap, handleRemoveLaunchSlot)}
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div className="truncate text-brand-dark dark:text-foreground">
                    {renderModelSelectOrText(sortedSlots[0], models, canEdit && !isSwapping && !!date, modelFormat, currentModel, handleSwap, handleLaunchSwap, handleRemoveLaunchSlot)}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      <div className="px-4">
        <Alert variant="warning" className="mt-4">
          <TriangleAlert className="h-4 w-4" />
          <AlertTitle>Format Schedule Notice</AlertTitle>
          <AlertDescription className="text-sm">
            Format schedules are subject to change at any time.
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}; 